/**
 * RS-5 — Submittals on the party's own link.
 *
 *   /site-office/:token/submittals
 *
 * What the firm still expects, and what has already gone in with the stamp it
 * came back with. A hand-in answers a register line when the firm wrote one,
 * and the next revision goes from the same row. Stamps and marks are RS-6.
 *
 * Rows come from GET /api/site-office-links/{token}/submittals. Files go up the
 * way the Messages card sends them, then their ids ride on the hand-in.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { cacheBranding } from '@/utils/skin';
import { SiteOfficeHead } from '../SiteOfficeSpecs';
import OfficeStrip, { parsePreview } from '@/components/site-office/OfficeStrip';
import { shortDate } from './OfficeQuestions';
import '@/styles/site-office.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const bare = axios.create();

// SO-10 — a document's own door on the party's link; the thread route is only
// for a file that came in on a message (an issued drawing did not).
const fileHref = (file, token) => (file.file_url
  ? `${API.replace(/\/api$/, '')}${file.file_url}`
  : `${API}/site-office-links/${token}/messages/${file.message_id}/files/${file.attachment_id}`);

/** One line the firm is still waiting on. */
export const ExpectedRow = ({ line, onHandIn, locked, lockedTitle }) => (
  <div className="kso-row" data-testid={`office-expected-${line.id}`}>
    <div className="t">
      <b>{line.title}</b>
      <span className="src">
        {[line.kind_label, line.for_label,
          line.due_date ? `due ${shortDate(line.due_date)}` : ''].filter(Boolean).join(' · ')}
      </span>
    </div>
    <button type="button" className="kso-btn" onClick={() => onHandIn(line)}
      disabled={Boolean(locked)} title={lockedTitle}
      data-testid={`office-handin-${line.id}`}>Hand in</button>
  </div>
);

/**
 * RS-7b — a shop drawing the firm drew and issued. The party says whether it
 * can be built as drawn, or sends it back with what needs changing.
 */
export const ConfirmRow = ({ row, token, onConfirm, onChange, locked, lockedTitle }) => {
  const file = (row.documents || [])[0];
  return (
    <div className="kso-row" data-testid={`office-confirm-${row.id}`}>
      <div className="t" style={{ display: 'block' }}>
        <b>{row.title}</b>
        <small>
          {[row.submittal_number, `rev ${row.revision_number}`,
            `from ${row.from_name || 'the firm'}`].filter(Boolean).join(' · ')}
        </small>
        <span className="kso-atts">
          {file ? (
            <a className="kso-btn" data-testid={`office-confirm-file-${row.id}`}
              href={fileHref(file, token)}
              target="_blank" rel="noreferrer">Open the drawing</a>
          ) : null}
          <button type="button" className="kso-btn brand" onClick={() => onConfirm(row)}
            disabled={Boolean(locked)} title={lockedTitle}
            data-testid={`office-can-build-${row.id}`}>Can build as drawn</button>
          <button type="button" className="kso-btn" onClick={() => onChange(row)}
            disabled={Boolean(locked)} title={lockedTitle}
            data-testid={`office-needs-change-${row.id}`}>Needs a change</button>
        </span>
      </div>
      <span className={`kso-tag ${row.status_tone}`} data-testid={`office-confirm-state-${row.id}`}>
        {row.status_label}
      </span>
    </div>
  );
};

/** One row that has gone in: its number, its revision, and the stamp so far. */
export const HandedRow = ({ row, token, onResubmit, locked, lockedTitle }) => {
  const file = (row.documents || [])[0];
  const stamped = (row.stamped || [])[0];
  return (
    <div className="kso-row" data-testid={`office-submittal-${row.id}`}>
      <div className="t" style={{ display: 'block' }}>
        <b>{row.title}</b>
        <small>
          {[row.submittal_number, `rev ${row.revision_number}`,
            row.for_label ? `for ${row.for_label}` : '', row.from_name,
            row.submitted_at ? `sent ${shortDate(row.submitted_at)}` : '']
            .filter(Boolean).join(' · ')}
        </small>
        {row.stamp_print ? (
          <small data-testid={`office-submittal-stamp-${row.id}`}>
            {`${row.stamp_print} · ${row.reviewer_name || 'the firm'}`
              + `${row.review_date ? ` · ${shortDate(row.review_date)}` : ''}`}
          </small>
        ) : null}
        {row.review_notes ? (
          <small data-testid={`office-submittal-notes-${row.id}`}>{row.review_notes}</small>
        ) : null}
        <span className="kso-atts">
          {file ? (
            <a className="kso-btn" data-testid={`office-submittal-marks-${row.id}`}
              href={fileHref(file, token)}
              target="_blank" rel="noreferrer">See the marks</a>
          ) : null}
          {stamped ? (
            <a className="kso-btn brand" data-testid={`office-submittal-stamped-${row.id}`}
              href={`${API}/site-office-links/${token}/submittals/${row.id}`
                + `/documents/${stamped.doc_index}/stamped`}
              target="_blank" rel="noreferrer">See the stamped copy</a>
          ) : null}
          {row.can_resubmit ? (
            <button type="button" className="kso-btn" onClick={() => onResubmit(row)}
              disabled={Boolean(locked)} title={lockedTitle}
              data-testid={`office-resubmit-${row.id}`}>
              {`Send rev ${row.revision_number + 1}`}
            </button>
          ) : null}
        </span>
      </div>
      <span className={`kso-tag ${row.status_tone}`} data-testid={`office-submittal-state-${row.id}`}>
        {row.status_label}
      </span>
    </div>
  );
};

export default function OfficeSubmittals() {
  const { token } = useParams();
  const navigate = useNavigate();
  // SO-9a — the firm reading this page from the preview: the strip travels, and
  // acting as the party is the party's alone.
  const [params] = useSearchParams();
  const preview = parsePreview(params.get('preview'));
  const [office, setOffice] = useState(null);
  const [data, setData] = useState(null);
  const [gone, setGone] = useState('');
  const [sheet, setSheet] = useState(null); // { line } | { again } | { change }
  const [asking, setAsking] = useState(null); // the row being confirmed
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState('shop_drawing');
  const [about, setAbout] = useState('');
  const [note, setNote] = useState('');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const [head, list] = await Promise.all([
        bare.get(`${API}/site-office-links/${token}`),
        bare.get(`${API}/site-office-links/${token}/submittals`),
      ]);
      setOffice(head.data);
      if (head.data?.branding) cacheBranding(head.data.branding);
      setData(list.data);
    } catch (_e) { setGone('This link is not valid'); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openSheet = (line) => {
    setSheet({ line });
    setTitle(line?.title || '');
    setKind(line?.kind || 'shop_drawing');
    setAbout(line?.for?.category_key ? `spec:${line.for.category_key}` : '');
    setNote('');
    setFiles([]);
    setError('');
  };
  const openAgain = (row) => {
    setSheet({ again: row });
    setTitle(row.title);
    setNote('');
    setFiles([]);
    setError('');
  };

  const openChange = (row) => {
    setSheet({ change: row });
    setTitle(row.title);
    setNote('');
    setFiles([]);
    setError('');
  };

  // RS-7b — "Can build as drawn", on one line, so nothing goes by accident.
  const confirmBuild = async () => {
    setSending(true);
    try {
      await bare.post(`${API}/site-office-links/${token}/submittals/${asking.id}/confirm`,
        { answer: 'confirmed', note: note.trim() });
      setAsking(null);
      setNote('');
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'That did not send. Try again.');
    }
    setSending(false);
  };

  const pick = (e) => {
    setFiles([...files, ...Array.from(e.target.files || [])].slice(0, 8));
    e.target.value = '';
  };

  const send = async () => {
    const back = sheet.change;
    if (back && !note.trim()) { setError('Say what needs changing'); return; }
    if (!back && !files.length) { setError('Add the PDF or photos first'); return; }
    setSending(true);
    setError('');
    try {
      let attachment_ids = [];
      if (files.length) {
        const form = new FormData();
        form.append('text', `Files for a submittal: ${title.slice(0, 70)}`);
        files.forEach((f) => form.append('files', f));
        const up = await bare.post(`${API}/site-office-links/${token}/messages/with-files`, form);
        attachment_ids = (up.data?.attachments || []).map((a) => a.id);
      }
      if (back) {
        await bare.post(`${API}/site-office-links/${token}/submittals/${back.id}/confirm`,
          { answer: 'needs_change', note: note.trim(), attachment_ids });
      } else if (sheet.again) {
        await bare.post(
          `${API}/site-office-links/${token}/submittals/${sheet.again.id}/resubmit`,
          { note: note.trim(), attachment_ids });
      } else {
        const [, code] = about ? about.split(':') : [];
        await bare.post(`${API}/site-office-links/${token}/submittals`, {
          register_line_id: sheet.line?.id,
          title: title.trim(),
          kind,
          note: note.trim(),
          for: code ? { category_key: code } : {},
          attachment_ids,
        });
      }
      setSheet(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'That did not send. Try again.');
    }
    setSending(false);
  };

  if (gone) {
    return (
      <main className="kso-page" data-testid="office-submittals">
        <div className="kso-wrap"><div className="kso-gate"><h1>{gone}</h1></div></div>
      </main>
    );
  }
  if (!office || !data) {
    return (
      <main className="kso-page" data-testid="office-submittals">
        <div className="kso-wrap"><div className="kso-gate"><p>Opening…</p></div></div>
      </main>
    );
  }

  const expected = data.expected || [];
  const handed = (data.handed_in || []).filter((r) => !r.can_confirm);
  const toConfirm = (data.handed_in || []).filter((r) => r.can_confirm);
  const firm = office.firm_name || 'the firm';
  const lockedTitle = preview ? `Only ${office.party?.name} can do this` : undefined;

  return (
    <main className="kso-page" data-testid="office-submittals">
      <div className="kso-wrap">
        {preview ? (
          <OfficeStrip projectId={preview.projectId} membershipId={preview.membershipId}
            projectName={office.project?.name} partyName={office.party?.name} page="Submittals" />
        ) : null}
        <SiteOfficeHead data={office} />
        <div className="kso-crumb" data-testid="office-submittals-crumb">
          <button type="button" className="back" data-testid="office-submittals-back"
            onClick={() => navigate(preview ? `/projects/${preview.projectId}/site-office/${preview.membershipId}?view=party` : `/site-office/${token}`)}>‹ Site Office</button>
          <span>Site Office</span><span>›</span><b>Submittals</b>
        </div>
        <div className="kso-grid">
          <section className="kso-card w12" data-testid="office-submittals-card">
            <div className="kso-card__hd">
              Submittals
              <span className="n">
                {[toConfirm.length ? `${toConfirm.length} for your confirmation` : '',
                  handed.length ? `${handed.length} in` : ''].filter(Boolean).join(' · ')
                  || 'none yet'}
              </span>
            </div>
            <div className="kso-card__bd">
              <p className="kso-hint" style={{ margin: '0 0 var(--km-s2)' }}>
                {`Hand in shop drawings, samples and reports for ${firm} to review.`}
              </p>
              {toConfirm.length ? (
                <>
                  <p className="kso-lab" data-testid="office-confirm-lab">For your confirmation</p>
                  {toConfirm.map((row) => (
                    <ConfirmRow key={row.id} row={row} token={token}
                      locked={Boolean(preview)} lockedTitle={lockedTitle}
                      onConfirm={(r) => { setAsking(r); setNote(''); setError(''); }}
                      onChange={openChange} />
                  ))}
                </>
              ) : null}
              <p className="kso-lab">Expected</p>
              {expected.length === 0 ? (
                <p className="kso-hint" style={{ margin: 0 }} data-testid="office-expected-none">
                  Nothing is outstanding.
                </p>
              ) : expected.map((line) => (
                <ExpectedRow key={line.id} line={line} onHandIn={openSheet}
                  locked={Boolean(preview)} lockedTitle={lockedTitle} />
              ))}
              <p className="kso-lab">Handed in</p>
              {handed.length === 0 ? (
                <p className="kso-hint" style={{ margin: 0 }} data-testid="office-submittals-none">
                  Nothing has gone in yet.
                </p>
              ) : handed.map((row) => (
                <HandedRow key={row.id} row={row} token={token} onResubmit={openAgain}
                  locked={Boolean(preview)} lockedTitle={lockedTitle} />
              ))}
              {expected.length === 0 && handed.length === 0 ? null : (
                <button type="button" className="kso-btn" onClick={() => openSheet(null)}
                  disabled={Boolean(preview)} title={lockedTitle}
                  data-testid="office-handin-free">Hand in something else</button>
              )}
            </div>
          </section>

          {asking ? (
            <section className="kso-card w12" data-testid="office-confirm-sheet">
              <div className="kso-card__hd">
                Can it be built as drawn?
                <span className="n">{asking.submittal_number}</span>
              </div>
              <div className="kso-card__bd">
                <div className="kso-form">
                  <p className="kso-hint" style={{ margin: 0 }}>
                    {`${firm} will accept it for fabrication once you confirm.`}
                  </p>
                  <input className="kso-in" value={note} maxLength={240}
                    placeholder="Anything to add (optional)"
                    onChange={(e) => setNote(e.target.value)}
                    data-testid="office-confirm-note" />
                  {error ? (
                    <p className="kso-hint kso-hint--late" data-testid="office-confirm-error">{error}</p>
                  ) : null}
                  <button type="button" className="kso-btn brand" disabled={sending}
                    onClick={confirmBuild} data-testid="office-confirm-send">
                    {sending ? 'Sending…' : 'Yes, it can be built'}
                  </button>
                  <button type="button" className="kso-btn" onClick={() => setAsking(null)}
                    data-testid="office-confirm-cancel">Not now</button>
                </div>
              </div>
            </section>
          ) : null}

          {sheet ? (
            <section className="kso-card w12" data-testid="office-handin-sheet">
              <div className="kso-card__hd">
                {sheet.change ? 'What needs changing?'
                  : sheet.again ? `Send rev ${sheet.again.revision_number + 1}` : 'Hand in'}
                <span className="n">
                  {sheet.change ? sheet.change.submittal_number
                    : sheet.again ? sheet.again.submittal_number : 'for review'}
                </span>
              </div>
              <div className="kso-card__bd">
                <div className="kso-form">
                  <input className="kso-in" placeholder="What is it?" value={title}
                    readOnly={!!sheet.again || !!sheet.line || !!sheet.change}
                    onChange={(e) => setTitle(e.target.value)}
                    data-testid="office-handin-title" />
                  {sheet.again || sheet.change ? null : (
                    <select className="kso-in" value={kind} disabled={!!sheet.line}
                      onChange={(e) => setKind(e.target.value)}
                      data-testid="office-handin-kind">
                      {(data.kinds || []).map((k) => (
                        <option key={k.key} value={k.key}>{k.label}</option>
                      ))}
                    </select>
                  )}
                  {sheet.again || sheet.line || sheet.change ? null : (
                    <select className="kso-in" value={about}
                      onChange={(e) => setAbout(e.target.value)}
                      data-testid="office-handin-for">
                      <option value="">For… (optional)</option>
                      {(data.about_options?.specs || []).map((o) => (
                        <option key={`spec:${o.id}`} value={`spec:${o.id}`}>
                          {`Spec · ${o.label}`}
                        </option>
                      ))}
                      {(data.about_options?.sheets || []).map((o) => (
                        <option key={`sheet:${o.id}`} value={`sheet:${o.id}`}>
                          {`Sheet · ${o.label}`}
                        </option>
                      ))}
                    </select>
                  )}
                  <textarea className="kso-in" rows={3} value={note}
                    placeholder={sheet.change
                      ? 'What has to change before it can be built'
                      : 'Anything the reviewer should know'}
                    onChange={(e) => setNote(e.target.value)}
                    data-testid="office-handin-note" />
                  {files.length ? (
                    <div data-testid="office-handin-files">
                      {files.map((f) => (
                        <span className="kso-tag" key={f.name}>
                          {f.name}
                          <button type="button" className="x" aria-label="Remove"
                            onClick={() => setFiles(files.filter((x) => x !== f))}>×</button>
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <label className="kso-btn" data-testid="office-handin-attach">
                    {sheet.change ? 'Add marked-up files (optional)' : 'Add the PDF or photos'}
                    <input type="file" accept="image/*,application/pdf" multiple hidden
                      onChange={pick} />
                  </label>
                  {error ? (
                    <p className="kso-hint kso-hint--late" data-testid="office-handin-error">{error}</p>
                  ) : null}
                  <button type="button" className="kso-btn brand" disabled={sending}
                    onClick={send} data-testid="office-handin-send">
                    {sending ? 'Sending…' : sheet.change ? 'Send it back' : 'Send for review'}
                  </button>
                  <button type="button" className="kso-btn" onClick={() => setSheet(null)}
                    data-testid="office-handin-cancel">Not now</button>
                </div>
              </div>
            </section>
          ) : null}
        </div>
      </div>
    </main>
  );
}
